import { useEffect, useRef, useState, type ReactNode } from "react";
import CustomMapMarker from "../../components/common/custom-map-marker";
import { useLocation, useNavigate } from "react-router-dom";
import myLocationIcon from "../../assets/svg/map/my-location.svg";
import { useRequestCurrentLocation } from "../../hooks/use-request-current-location";
import LocationButton from "../../components/common/location-button";
import { usePullToRefresh } from "../../core/pull-to-refresh-provider";
import { isDev, isWeb } from "../../utils/user-agent";
import AppDownloadModal from "../../components/app-download/app-download-modal";
import {
  getMapPosition,
  MAP_MY_LOCATION_BUTTON_ZOOM,
  setMapPosition as persistMapPosition,
  getMyLocation,
  setMyLocation,
} from "./utils/map-position-storage";
import {
  useMapStores,
  type MapAvailableFilter,
} from "./hooks/use-map-stores";
import { useMapStoreDetail } from "./hooks/use-map-store-detail";
import MapLoadingOverlay from "./components/map-loading-overlay";
import MapRedoSearchChip from "./components/map-redo-search-chip";
import MapStoreCard from "./components/map-store-card";
import type { Store } from "../../__generated__/graphql";
import {
  DEFAULT_MAP_LAT,
  DEFAULT_MAP_LNG,
} from "../../utils/map-defaults";
import { processAvailableFilter } from "../home/utils/store-filter";
import type { PickupTimeFilter } from "../home/hooks/use-pickup-time-filter";
import { readPickupTimeFilterFromStorage } from "../home/hooks/use-pickup-time-filter";

const MY_LOCATION_MARKER_ICON_PX = 24;

const MARKER_PAN_DURATION_MS = 450;

function createMyLocationMarkerIcon(maps: typeof naver.maps) {
  const s = MY_LOCATION_MARKER_ICON_PX;
  return {
    url: myLocationIcon,
    size: new maps.Size(s, s),
    scaledSize: new maps.Size(s, s),
    anchor: new maps.Point(s / 2, s / 2),
  };
}

function Map({
  customHeight,
  zoom,
  zoomControl,
  availableFilter: availableFilterProp,
  pickupTimeFilter: pickupTimeFilterProp,
  topOverlay,
}: {
  customHeight?: undefined | number;
  zoom?: undefined | number;
  zoomControl?: undefined | boolean;
  /** 미전달 시 processAvailableFilter()와 동일 */
  availableFilter?: MapAvailableFilter;
  /** 미전달 시 readPickupTimeFilterFromStorage() */
  pickupTimeFilter?: PickupTimeFilter | null;
  /** 지도 영역 상단(탭 바로 아래) 오버레이 — 로딩 오버레이보다 위에 표시 */
  topOverlay?: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const mapElement = useRef<HTMLDivElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [clickedStoreId, setClickedStoreId] = useState<number | null>(null);
  const [clickedStore, setClickedStore] = useState<Store | null>(null);
  const [mapPosition, setMapPosition] = useState(getMapPosition);
  const requestCurrentLocation = useRequestCurrentLocation();
  const { naver } = window;
  const [newMap, setNewMap] = useState<naver.maps.Map | null>(null);
  const [myLocationMarker, setMyLocationMarker] =
    useState<naver.maps.Marker | null>(null);
  const { setIsPullable } = usePullToRefresh();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const markersRef = useRef<naver.maps.Marker[]>([]);
  const [showResearchChip, setShowResearchChip] = useState(false);
  /** 마커 클릭 후 panTo 시 발생하는 dragstart로 카드가 지워지지 않도록 */
  const suppressSelectionClearUntilRef = useRef(0);

  const availableFilter =
    availableFilterProp ?? processAvailableFilter();
  const pickupTimeFilter =
    pickupTimeFilterProp !== undefined
      ? pickupTimeFilterProp
      : readPickupTimeFilterFromStorage();

  const { displayStores, isInitialLoading, isRedoFetching, searchHere } =
    useMapStores({ availableFilter, pickupTimeFilter });
  const { getStoreDetail, storeDetailData, getStoreFromCache } =
    useMapStoreDetail();

  const isStoresLoading = isInitialLoading || isRedoFetching;

  useEffect(() => {
    if (storeDetailData?.store) {
      setClickedStore(storeDetailData.store as Store);
    }
  }, [storeDetailData]);

  useEffect(() => {
    if (!clickedStoreId) setClickedStore(null);
  }, [clickedStoreId]);

  useEffect(() => {
    if (!mapElement.current || !naver) return;

    const center = new naver.maps.LatLng(mapPosition.lat, mapPosition.lng);
    const mapOptions: naver.maps.MapOptions = {
      center,
      zoom: zoom ?? mapPosition.zoom,
      minZoom: 7,
      maxZoom: 19,
      zoomControl,
      zoomControlOptions: {
        style: naver.maps.ZoomControlStyle.SMALL,
        position: naver.maps.Position.TOP_RIGHT,
      },
      mapDataControl: false,
      scaleControl: false,
    };

    const map = new naver.maps.Map(mapElement.current, mapOptions);
    setNewMap(map);
    const myLocSaved = getMyLocation();
    const showDevWebPreviewMarker = !myLocSaved && isWeb() && isDev();
    const myLoc =
      myLocSaved ??
      (showDevWebPreviewMarker
        ? { lat: DEFAULT_MAP_LAT, lng: DEFAULT_MAP_LNG }
        : null);

    if (myLoc) {
      const position = new naver.maps.LatLng(myLoc.lat, myLoc.lng);
      const locationMarker = new naver.maps.Marker({
        position,
        map,
        icon: createMyLocationMarkerIcon(naver.maps),
      });
      locationMarker.setTitle(
        showDevWebPreviewMarker ? "my_location_preview" : "my_baby",
      );
      setMyLocationMarker(locationMarker);
    }

    return () => {
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      map.destroy();
    };
    // 마운트/언마운트 및 location.key, zoom, zoomControl 변경 시에만 지도 재생성. mapPosition은 초기값만 사용
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, zoom, zoomControl]);

  /** Flex·% 높이 확정 전에 지도가 0×0으로 초기화되는 경우 타일이 안 그려짐 → 레이아웃 후 크기 재동기화 */
  useEffect(() => {
    if (!newMap) return;

    const syncSize = () => {
      const resize = (
        newMap as unknown as { autoResize?: () => void }
      ).autoResize;
      resize?.call(newMap);
    };

    syncSize();
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(syncSize);
    });

    const container = mapContainerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return () => cancelAnimationFrame(raf);
    }

    const ro = new ResizeObserver(() => {
      syncSize();
    });
    ro.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [newMap]);

  useEffect(() => {
    if (!newMap) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (displayStores.length === 0) return;

    displayStores.forEach((store: Store, index: number) => {
      const { title, lat, lng } = store;
      try {
        const newMarker = new naver.maps.Marker({
          position: new naver.maps.LatLng(lat as number, lng as number),
          map: newMap,
          title,
          clickable: true,
          icon: {
            content: CustomMapMarker({ staggerIndex: index }),
            size: new naver.maps.Size(38, 58),
            anchor: new naver.maps.Point(10, 30),
          },
        });
        newMarker.setTitle(title);
        newMarker.addListener("click", () => {
          const storeLatLng = new naver.maps.LatLng(
            lat as number,
            lng as number,
          );
          suppressSelectionClearUntilRef.current =
            Date.now() + MARKER_PAN_DURATION_MS + 120;

          const storeId = store.id as number;
          setClickedStoreId(storeId);
          const cached = getStoreFromCache(storeId);
          if (cached) setClickedStore(cached);
          else getStoreDetail({ variables: { storeId } });

          newMap.panTo(storeLatLng, { duration: MARKER_PAN_DURATION_MS });
        });
        markersRef.current.push(newMarker);
      } catch {
        // 마커 생성 실패 시 무시
      }
    });
    // displayStores, newMap만 의존. naver.maps/ getStoreDetail/getStoreFromCache는 전역·stable 참조
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayStores, newMap]);

  useEffect(() => {
    setIsPullable(false);
    return () => setIsPullable(true);
  }, [setIsPullable]);

  useEffect(() => {
    if (!newMap) return;

    const listeners = [
      naver.maps.Event.addListener(newMap, "dragstart", () => {
        if (Date.now() < suppressSelectionClearUntilRef.current) {
          return;
        }
        setClickedStoreId(null);
        setClickedStore(null);
      }),
      naver.maps.Event.addListener(newMap, "dragend", () => {
        const center = newMap.getCenter();
        const z = newMap.getZoom();
        setShowResearchChip(true);
        setMapPosition((prev) => ({
          ...prev,
          lat: center.y,
          lng: center.x,
          zoom: z,
        }));
        persistMapPosition({ lat: center.y, lng: center.x, zoom: z });
      }),
      naver.maps.Event.addListener(newMap, "zoom_changed", () => {
        const z = newMap.getZoom();
        setMapPosition((prev) => ({ ...prev, zoom: z }));
        persistMapPosition({ zoom: z });
      }),
    ];

    return () => {
      listeners.forEach((l) => naver.maps.Event.removeListener(l));
    };
    // naver.maps.Event는 전역 객체라 의존성에서 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newMap]);

  const handleRedoSearch = async () => {
    if (!newMap) return;
    const center = newMap.getCenter();
    try {
      await searchHere(center.y, center.x);
      setShowResearchChip(false);
    } catch {
      // 네트워크 오류 등 시 칩 유지
    }
  };

  const onClickStore = (id: number) => {
    navigate(`/store/${id}`);
  };

  const onClickMyLocation = () => {
    if (isWeb()) {
      setIsModalOpen(true);
      return;
    }
    requestCurrentLocation((coords) => {
      const latLng = new naver.maps.LatLng(coords.latitude, coords.longitude);

      setMapPosition((prev) => ({
        ...prev,
        lat: coords.latitude,
        lng: coords.longitude,
        zoom: MAP_MY_LOCATION_BUTTON_ZOOM,
      }));
      setMyLocation(coords.latitude, coords.longitude);

      newMap?.setZoom(MAP_MY_LOCATION_BUTTON_ZOOM);
      newMap?.panTo(latLng, { duration: 0 });

      if (myLocationMarker?.getTitle() === "my_baby") {
        myLocationMarker.setPosition(latLng);
        setMyLocationMarker(myLocationMarker);
      } else if (newMap) {
        const locationMarker = new naver.maps.Marker({
          position: latLng,
          map: newMap,
          icon: createMyLocationMarkerIcon(naver.maps),
        });
        locationMarker.setTitle("my_baby");
        setMyLocationMarker(locationMarker);
      }

      void searchHere(coords.latitude, coords.longitude).finally(() =>
        setShowResearchChip(false),
      );
    });
  };

  const computedHeightPx = customHeight ?? null;

  return (
    <div
      ref={mapContainerRef}
      className={
        computedHeightPx != null
          ? "relative w-[100%]"
          : "relative h-full min-h-0 w-full"
      }
      style={
        computedHeightPx != null
          ? { height: `${computedHeightPx}px` }
          : undefined
      }
    >
      {/* 네이버 지도만 이 노드에 마운트 — 내부에 React 오버레이를 두면 타일 레이어에 가려짐 */}
      <div
        ref={mapElement}
        id="map"
        className="absolute inset-0 h-full w-full"
      />

      <MapLoadingOverlay isLoading={isStoresLoading} />

      {topOverlay ? (
        <div className="pointer-events-none absolute left-0 right-0 top-0 z-[55] px-3 pb-1 pt-1.5">
          <div className="pointer-events-auto overflow-visible">
            {topOverlay}
          </div>
        </div>
      ) : null}

      <MapStoreCard store={clickedStore} onStoreClick={onClickStore} />

      <MapRedoSearchChip
        visible={showResearchChip && clickedStoreId === null}
        loading={isRedoFetching}
        onClick={() => void handleRedoSearch()}
      />

      <LocationButton
        onClick={onClickMyLocation}
        variant="default"
        className="absolute bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] right-6 z-30"
      />
      <AppDownloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        where="지도 내 내위치 찾기 버튼"
      />
    </div>
  );
}

export default Map;
